const restoreStateKey = 'plp-scroll-restore';

class BackToTopButton extends HTMLElement {
  /** @type {MutationObserver | null} */
  #resultsObserver = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #resultsList = null;

  /** @type {boolean} */
  #hasLoadedAdditionalPage = false;

  /** @type {number | null} */
  #showScrollThreshold = null;

  connectedCallback() {
    this.#button = this.querySelector('[data-back-to-top-trigger]');
    if (!(this.#button instanceof HTMLButtonElement) || !document.body) return;

    this.#button.hidden = true;
    this.#button.addEventListener('click', this.#handleClick);
    window.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#resultsList = this.#getResultsList();
    if (this.#resultsList) {
      this.#resultsObserver = new MutationObserver(() => {
        this.#syncLoadedPageState();
      });

      this.#resultsObserver.observe(this.#resultsList, {
        attributes: true,
        attributeFilter: ['data-page'],
        childList: true,
        subtree: true,
      });
    }

    this.#syncLoadedPageState();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    window.removeEventListener('scroll', this.#handleScroll);
    this.#resultsObserver?.disconnect();
    this.#resultsObserver = null;
    this.#button = null;
    this.#resultsList = null;
    this.#showScrollThreshold = null;
  }

  #getResultsList() {
    const previousSibling = this.previousElementSibling;

    if (previousSibling instanceof HTMLElement && previousSibling.tagName.toLowerCase() === 'results-list') {
      return previousSibling;
    }

    const resultsList = this.parentElement?.querySelector('results-list');
    return resultsList instanceof HTMLElement ? resultsList : null;
  }

  #syncLoadedPageState() {
    const cards = this.#resultsList?.querySelectorAll('[ref="cards[]"][data-page]') ?? [];
    const loadedPages = new Set();

    for (const card of cards) {
      const page = card.getAttribute('data-page');
      if (page) {
        loadedPages.add(page);
      }
    }

    this.#hasLoadedAdditionalPage = loadedPages.size > 1;
    if (!this.#hasLoadedAdditionalPage) {
      this.#showScrollThreshold = null;
    }

    this.#updateVisibility();
  }

  #updateVisibility() {
    if (!this.#button) return;

    if (!this.#hasLoadedAdditionalPage) {
      this.#button.hidden = true;
      return;
    }

    const scrollY = window.scrollY;

    if (this.#showScrollThreshold === null && scrollY > 1) {
      this.#showScrollThreshold = scrollY;
    }

    this.#button.hidden = this.#showScrollThreshold === null || scrollY < this.#showScrollThreshold;
  }

  #handleScroll = () => {
    this.#updateVisibility();
  };

  #handleClick = () => {
    sessionStorage.removeItem(restoreStateKey);
    document.documentElement.classList.remove('plp-restore-pending');

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }

    window.dispatchEvent(new CustomEvent('back-to-top:before-scroll'));
    window.scrollTo({ top: 0, left: 0 });
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('back-to-top:after-scroll'));
    });
  };
}

if (!customElements.get('back-to-top-button')) {
  customElements.define('back-to-top-button', BackToTopButton);
}